/**
 * Video Embed Component for Rich Content Integration
 * 
 * A flexible video embedding component that supports multiple video platforms
 * and provides responsive, accessible video integration for Sanity CMS content.
 * 
 * Supported Platforms:
 * - YouTube: Standard and embedded URLs
 * - Vimeo: Standard video URLs
 * - Direct video files: MP4, WebM formats
 * - Extensible for additional providers
 * 
 * Features:
 * - Responsive aspect ratio containers (16:9, 4:3, 1:1, 9:16)
 * - Multiple size options (small, medium, large, full)
 * - Automatic video ID extraction from URLs
 * - Iframe sandbox security for external embeds
 * - Native video controls for direct files
 * - Fallback error handling for invalid URLs
 * 
 * Responsive Design:
 * - CSS aspect ratio padding for consistent layouts
 * - Flexible sizing system with predefined breakpoints
 * - Mobile-optimized controls and playback
 * - Tailwind CSS responsive utilities
 * 
 * Security Features:
 * - Iframe sandbox attributes for external embeds
 * - URL validation and sanitization
 * - Safe iframe embedding practices
 * 
 * @param {object} value - Video configuration object from Sanity CMS
 * @param {string} value.url - Video URL (YouTube, Vimeo, or direct file)
 * @param {string} value.videoProvider - Platform type ('youtube', 'vimeo', 'direct')
 * @param {string} value.aspectRatio - Aspect ratio ('16:9', '4:3', '1:1', '9:16')
 * @param {string} value.size - Display size ('small', 'medium', 'large', 'full')
 * 
 * @description Responsive video embed component with multi-platform support
 */

import React from 'react';

const VideoEmbed = ({ value }) => {
  if (!value?.url) {
    return <div>Missing Video URL</div>;
  }

  const { url, videoProvider = 'youtube', aspectRatio = '16:9', size = 'medium' } = value;
  
  // Calculate aspect ratio padding
  const getAspectRatioPadding = (ratio = '16:9') => {
    switch (ratio) {
      case '4:3':
        return 'pb-[75%]'; // 4:3 = 75%
      case '1:1':
        return 'pb-[100%]'; // 1:1 = 100%
      case '9:16':
        return 'pb-[177.78%]'; // 9:16 = 177.78%
      case '16:9':
      default:
        return 'pb-[56.25%]'; // 16:9 = 56.25%
    }
  };

  // Get width class based on size
  const getWidthClass = (size) => {
    switch (size) {
      case 'small':
        return 'max-w-xs mx-auto'; // 25%
      case 'medium':
        return 'max-w-2xl mx-auto'; // 50%
      case 'large':
        return 'max-w-4xl mx-auto'; // 75%
      case 'full':
        return 'w-full'; // 100%
      default:
        return 'max-w-2xl mx-auto';
    }
  };

  const aspectRatioPadding = getAspectRatioPadding(aspectRatio);
  const widthClass = getWidthClass(size);

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Extract Vimeo video ID
  const getVimeoId = (url) => {
    const regExp = /^.*(vimeo\.com\/)((channels\/[A-z]+\/)|(groups\/[A-z]+\/videos\/))?([0-9]+)/;
    const match = url.match(regExp);
    return match && match[5] ? match[5] : null;
  };

  // Determine the embed URL based on provider
  let embedUrl = '';
  let isIframe = true;

  if (videoProvider === 'youtube') {
    const videoId = getYouTubeId(url);
    if (!videoId) return <div>Invalid YouTube URL</div>;
    
    // Extract timestamp parameters
    let startTime = '';
    
    // Check for t parameter (e.g., ?t=1m30s or ?t=90)
    const tParam = url.match(/[?&]t=([^&]*)/);
    if (tParam && tParam[1]) {
      // Convert to seconds if needed
      let seconds = 0;
      
      if (tParam[1].includes('m') || tParam[1].includes('h') || tParam[1].includes('s')) {
        // Format like 1h2m3s
        const hours = tParam[1].match(/(\d+)h/);
        const minutes = tParam[1].match(/(\d+)m/);
        const secs = tParam[1].match(/(\d+)s/);
        
        if (hours) seconds += parseInt(hours[1]) * 3600;
        if (minutes) seconds += parseInt(minutes[1]) * 60;
        if (secs) seconds += parseInt(secs[1]);
      } else {
        // Format is just a number in seconds
        seconds = parseInt(tParam[1]);
      }
      
      if (seconds > 0) {
        startTime = `?start=${seconds}`;
      }
    }
    
    // Check for start parameter (e.g., ?start=90)
    const startParam = url.match(/[?&]start=(\d+)/);
    if (!startTime && startParam && startParam[1]) {
      startTime = `?start=${startParam[1]}`;
    }
    
    embedUrl = `https://www.youtube.com/embed/${videoId}${startTime}`;
  } else if (videoProvider === 'vimeo') {
    const videoId = getVimeoId(url);
    if (!videoId) return <div>Invalid Vimeo URL</div>;
    embedUrl = `https://player.vimeo.com/video/${videoId}`;
  } else if (videoProvider === 'custom') {
    // For custom video URLs, we'll try to display them directly if they're video files
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov'];
    const hasVideoExtension = videoExtensions.some(ext => url.toLowerCase().includes(ext));
    
    if (hasVideoExtension) {
      isIframe = false;
      embedUrl = url;
    } else {
      // If it doesn't have a video extension, try to embed it as an iframe
      embedUrl = url;
    }
  }

  return (
    <div className={`my-8 ${widthClass}`}>
      <div className={`relative w-full ${aspectRatioPadding}`}>
        {isIframe ? (
          <iframe
            className="absolute top-0 left-0 w-full h-full rounded-lg"
            src={embedUrl}
            title={value.caption || 'Video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        ) : (
          <video
            className="absolute top-0 left-0 w-full h-full rounded-lg"
            src={embedUrl}
            controls
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      {value.caption && (
        <div className="mt-2 text-sm text-gray-600 italic text-center">
          {value.caption}
        </div>
      )}
    </div>
  );
};

export default VideoEmbed; 